import { Stack } from '@mui/material';
import type { FormikHelpers } from 'formik';
import { Form, Formik } from 'formik';
import { navigate } from 'gatsby';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import type { Flags, Schema } from 'yup';

import RecordAttachmentForm from '@components/Attachments/RecordAttachmentForm';
import DrilldownItem from '@components/DrilldownItem';
import { FormikFhirDateField, FormikTextField } from '@components/FormikFields';
import { RecordSubmitButton } from '@components/RecordSubmitButton';
import RelatedRecordsForm from '@components/RelatedRecordsTable/RelatedRecordsForm';

import { MEDICATIONS } from '@constants/routes';

import {
  useCreateMedication,
  useUpdateMedicationStatement,
} from '@hooks/api/medication';
import { useMedicationSchema } from '@hooks/schemas/useMedicationSchema';
import useFhirRecordForm from '@hooks/useFhirRecordForm';
import { useFormat } from '@hooks/useFormat';
import { useIsMobile } from '@hooks/useIsMobile';
import { useSnackbar } from '@hooks/useSnackbar';

import { HorizontalRule } from '@layouts/AuthLayout';
import { ColumnLayout } from '@layouts/DrilldownStyle';

import { getMedicationTemplate } from '@models/fhir-templates/medication';
import {
  MEDICATION_CATEGORY_MAP,
  MEDICATION_STATUS_MAP,
  type FormValues,
  type MedicationStatementRecord,
} from '@models/medication';
import type { DrilldownProps } from '@models/record-drilldown';

import {
  emptyDosageStep,
  type DosageStep,
} from '@utils/fhir-mappers/dosageSteps';
import {
  blankProductNames,
  emptyIngredientRow,
  hasMedicineAmount,
  inferDoseForm,
  type MedicationIngredientRow,
} from '@utils/fhir-mappers/medicationIngredients';
import { getRecordSourceForSubmit } from '@utils/getRecordSourceForSubmit';
import { stripEmptyStrings } from '@utils/strip';

import SharingDrilldownWrapper from '@views/Records/Common/SharingDrilldownWrapper';

import DrilldownWrapper from '../Common/DrilldownWrapper';
import DosageSteps from './DosageSteps';
import MedicineAmount from './MedicineAmount';

const messages = defineMessages({
  frequency: {
    id: 'medicationsDrilldown.frequency',
    defaultMessage: 'Frequency',
  },
  dateStopped: {
    id: 'medicationsDrilldown.dateStopped',
    defaultMessage: 'End Date',
  },
  dateStarted: {
    id: 'medicationsDrilldown.dateStarted',
    defaultMessage: 'Start Date',
  },
  instructions: {
    id: 'medicationsDrilldown.instructions',
    defaultMessage: 'Instructions',
  },
  acquisitionType: {
    id: 'medicationsDrilldown.category',
    defaultMessage: 'Category',
  },
  doseAmount: {
    id: 'medicationsDrilldown.doseAmount',
    defaultMessage: 'Dose Amount',
  },
  doseUnit: {
    id: 'medicationsDrilldown.doseUnit',
    defaultMessage: 'Unit',
  },
  route: {
    id: 'medicationsDrilldown.route',
    defaultMessage: 'Route',
  },
  required: {
    id: 'GLOBAL.required',
    defaultMessage: 'This field is required',
  },
  error: {
    id: 'GLOBAL.error',
    defaultMessage: 'An error occurred. Please try again later.',
  },
  medication: {
    id: 'medicationsDrilldown.medication',
    defaultMessage: 'Medication',
  },
  status: {
    id: 'medicationsMain.status',
    defaultMessage: 'Status',
  },
  frequencyTimes: {
    id: 'medicationsDrilldown.frequencyTimes',
    defaultMessage: 'Times',
  },
  frequencyEvery: {
    id: 'medicationsDrilldown.frequencyEvery',
    defaultMessage: 'Every',
  },
  frequencyUnit: {
    id: 'medicationsDrilldown.frequencyUnit',
    defaultMessage: 'Unit',
  },
  timing: {
    id: 'medicationsDrilldown.timing',
    defaultMessage: 'Timing',
  },
  duration: {
    id: 'medicationsDrilldown.duration',
    defaultMessage: 'Duration',
  },
  rangeInvalid: {
    id: 'medicationsDrilldown.rangeInvalid',
    defaultMessage:
      'The high amount must be greater than or equal to the low amount',
  },
  mustBePositive: {
    id: 'medicationsDrilldown.mustBePositive',
    defaultMessage: 'Enter a number greater than zero',
  },
  everyRequired: {
    id: 'medicationsDrilldown.everyRequired',
    defaultMessage: 'Enter the lower bound of the interval as well',
  },
  amountRequired: {
    id: 'medicationsDrilldown.amountRequired',
    defaultMessage: 'Enter a strength, a dose, or both',
  },
  /**
   * A ceiling with no unit isn't written at all, and `maxDosePerPeriod` is a
   * form-owned field, so saving would delete one the record already had. Block
   * instead: silently dropping a dose limit is not something to do quietly.
   */
  ceilingNeedsForm: {
    id: 'medicationsDrilldown.ceilingNeedsForm',
    defaultMessage:
      'A dose limit needs a unit. Pick a form other than Unknown, or clear the limit.',
  },
});

interface MedicationDrilldownProps extends DrilldownProps<MedicationStatementRecord> {
  isPatient: boolean;
  /**
   * When true, suppresses the default redirect to the MEDICATIONS route
   * after successfully creating a new medication.
   * - Default: false (keeps existing behavior in normal routes)
   * - In auto-curation flow, this should be set to true.
   */
  disableNavigateOnCreate?: boolean;
}

const Medication: React.FC<MedicationDrilldownProps> = ({
  data,
  loading: baseLoading,
  isSharing = false,
  onSubmit,
  isCreating,
  isEditing,
  patientId: patient_id,
  isCurator,
  isPatient,
  disableNavigateOnCreate = false,
}) => {
  const { formatMessage } = useIntl();
  const { formatDateLabel } = useFormat();
  const { enqueueSnackbar } = useSnackbar();
  const isMobile = useIsMobile('lg');

  const [createMedication, createMedicationLoading] = useCreateMedication();
  const [updateMedication, updateMedicationLoading] =
    useUpdateMedicationStatement();
  const {
    formikHelpers: { baseFhirValidationSchema, initialFhirFormValues },
    loading: recordStatusAndSourceLoading,
    handleRecordFilesSourcePublishing,
  } = useFhirRecordForm();
  const medValidationSchema = useMedicationSchema();
  const isModifying = isCreating || isEditing;

  const loading =
    baseLoading ||
    updateMedicationLoading ||
    createMedicationLoading ||
    recordStatusAndSourceLoading;

  // MedicationStatementRecord always builds these two arrays, but auto-curation
  // renders this form with a plain object assembled from OCR instead, passed
  // through an untyped React.FC<any> in RESOURCE_FORM_MAP. That object only
  // carries a key when Discern found something to put in it, so both can be
  // missing however the types read (ENGAGE-37K).
  const storedSteps: DosageStep[] | undefined = data?.dosageSteps;
  const storedRows: MedicationIngredientRow[] | undefined = data?.ingredients;

  // The Form dropdown is new; a stored record keeps the same word somewhere
  // else, so seed from wherever it actually is.
  const storedDoseForm = inferDoseForm(
    data?.doseForm,
    storedSteps?.[0]?.doseUnit,
    storedRows
  );

  const initialValues: FormValues = {
    ...initialFhirFormValues,
    title: data?.medication ?? '',
    acquisitionType: data?.acquisitionType ?? '',
    endingDate: data?.endingDate ?? '',
    // Length, not nullish: an auto-curation record can seed an empty array, and
    // the fields below bind to dosageSteps.0.*, so there has to be a step.
    dosageSteps: storedSteps?.length ? storedSteps : [emptyDosageStep()],
    // One row seeded so a single-ingredient product has somewhere to put its
    // strength; the row is dropped on save if left blank. A row whose stored
    // name is just the product name is shown with an empty ingredient field, so
    // it reads as "unnamed" rather than as a breakdown someone recorded — the
    // save writes the same name straight back.
    ingredients: storedRows?.length
      ? blankProductNames(storedRows, data?.medication)
      : [emptyIngredientRow()],
    doseForm: storedDoseForm,
    instructions: data?.instructions ?? '',
    route: data?.route ?? '',
    startingDate: data?.startingDate ?? '',
    selfReported: data?.selfReported ?? false,
    associatedResources: [],
    status: data?.status ?? '',
    isPatient,
    requireAmount:
      Boolean(isCreating) ||
      hasMedicineAmount(storedRows, storedSteps?.[0]?.doseValue),
  };

  const validationSchema = medValidationSchema.concat(
    baseFhirValidationSchema as typeof medValidationSchema
  ) as unknown as Schema<Partial<FormValues>, any, any, Flags>;

  async function handleSubmit(
    values: FormValues,
    { validateForm }: FormikHelpers<FormValues>
  ) {
    if (!patient_id) {
      return;
    }
    // Keep dosageSteps and ingredients out of the strip so their fields (e.g.
    // empty timing arrays, half-filled ingredient rows) retain the shapes the
    // mappers rely on.
    const { dosageSteps, ingredients, ...rest } = values;
    values = { ...stripEmptyStrings(rest), dosageSteps, ingredients };
    await validateForm(values);

    let med: MedicationStatementRecord | null | undefined;
    try {
      if (data?.id) {
        const { data: updatedMed } = await updateMedication({
          input: {
            medication_statement_id: data.id,
            medicationstatement: data.getModelInput(values),
          },
        });
        med = updatedMed;
      } else {
        const { data } = await createMedication({
          input: {
            ...getMedicationTemplate(patient_id, values),
          },
        });
        med = data;
      }
    } catch {
      enqueueSnackbar({
        variant: 'error',
        message: formatMessage(messages.error),
      });
    } finally {
      await handleRecordFilesSourcePublishing({
        resource: med?.getResourceId(),
        files: values.files,
        toPublish: values.toPublish,
        recordSource: getRecordSourceForSubmit({
          isCurator,
          isCreating,
          initialRecordSource: initialValues.recordSource,
          currentRecordSource: values.recordSource,
        }),
      });
      // Existing behavior preserved by default.
      // In auto-curation flow, disableNavigateOnCreate=true will skip this.
      if (med && isCreating && !disableNavigateOnCreate) {
        navigate(`${MEDICATIONS}${med.id}`);
      }
      onSubmit?.();
    }
  }

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {({ setFieldValue, values }) => {
        const children = (
          <>
            {/* Form, then strength and dose as one "at least one" block. */}
            <MedicineAmount isEditing={Boolean(isModifying)} />

            {/* Frequency, timing & duration for a single dosage. */}
            <DosageSteps isEditing={Boolean(isModifying)} />

            {/* Route + Category */}
            <ColumnLayout>
              <DrilldownItem
                label={formatMessage(messages.route)}
                name="route"
                value={data?.route}
              />
              <DrilldownItem
                label={formatMessage(messages.acquisitionType)}
                name="acquisitionType"
                onChange={(value) => setFieldValue('acquisitionType', value)}
                options={Object.entries(MEDICATION_CATEGORY_MAP).map(
                  ([k, v]) => ({ k, v })
                )}
                type="dropdown"
                value={data?.acquisitionTypeName}
              />
            </ColumnLayout>

            <ColumnLayout>
              <DrilldownItem
                label={formatMessage(messages.status)}
                name="status"
                onChange={(value) => {
                  setFieldValue('status', value);
                }}
                options={Object.entries(MEDICATION_STATUS_MAP).map(
                  ([k, v]) => ({
                    k,
                    v,
                  })
                )}
                type="dropdown"
                value={data?.status}
              />
            </ColumnLayout>

            <ColumnLayout>
              <DrilldownItem
                label={formatMessage(messages.instructions)}
                name="instructions"
                type="text-area"
                value={data?.instructions}
              />
            </ColumnLayout>

            <RelatedRecordsForm associations={values.associatedResources} />

            <RecordAttachmentForm
              isCreating={isCreating}
              onFileChange={(files: DroppedFile[]) => {
                setFieldValue('files', files);
              }}
              patientId={patient_id}
              record={{
                attachments: data?.attachments,
                resourceId: data?.getResourceId(),
              }}
            />

            {isModifying && (
              <>
                <HorizontalRule />
                <RecordSubmitButton />
              </>
            )}
          </>
        );
        const drilldownProps = {
          loading,
          resource: data,
          selfReported: data?.selfReported,
          subtitle: isModifying ? (
            <Stack
              direction={isMobile ? 'column' : 'row'}
              columnGap={isMobile ? 0 : 2}
            >
              <FormikFhirDateField
                formats={['date', 'year']}
                label={formatMessage(messages.dateStarted)}
                name="startingDate"
                onChange={(value) => {
                  setFieldValue('startingDate', value);
                }}
                value={data?.startingDate}
                max={true}
              />
              <FormikFhirDateField
                formats={['date', 'year']}
                label={formatMessage(messages.dateStopped)}
                name="endingDate"
                onChange={(value) => {
                  setFieldValue('endingDate', value);
                }}
                value={data?.endingDate}
                min={values.startingDate}
              />
            </Stack>
          ) : (
            [
              formatDateLabel(messages.dateStarted, data?.getDateStartStr()),
              formatDateLabel(messages.dateStopped, data?.getDateEndStr()),
            ]
          ),
          title: data?.medication,
          titleInput: (
            <FormikTextField
              name="title"
              placeholder={formatMessage(messages.medication)}
            />
          ),
        };
        return (
          <Form>
            {isSharing ? (
              <SharingDrilldownWrapper {...drilldownProps}>
                {children}
              </SharingDrilldownWrapper>
            ) : (
              <DrilldownWrapper
                // actions={[
                //   {
                //     title: 'edit',
                //     handleClick: () => {
                //       navigate(`${ROUTES.EDIT_MEDICATIONS}${id}`);
                //     },
                //   },
                // ]}
                associatedRecords={data?.associatedRecords}
                backRoute={MEDICATIONS}
                notes={data?.recordNotes}
                {...drilldownProps}
              >
                {children}
              </DrilldownWrapper>
            )}
          </Form>
        );
      }}
    </Formik>
  );
};

export default Medication;
